import { useState } from "react";
import { Navigate, useNavigate } from "react-router-dom";
import { useAuth } from "@/contexts/AuthContext";
import { Logo } from "@/components/Logo";
import { LogIn, Lock, Mail } from "lucide-react";
import { toast } from "sonner";

export default function AdminLogin() {
  const { user, login, loading } = useAuth();
  const nav = useNavigate();
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState("");

  if (loading) return <div className="p-10">Loading…</div>;
  if (user) return <Navigate to="/admin" replace />;

  const submit = async (e) => {
    e.preventDefault();
    setSubmitting(true);
    setError("");
    const res = await login(email, password);
    setSubmitting(false);
    if (res.ok) {
      toast.success("Welcome back, admin");
      nav("/admin");
    } else {
      setError(res.error);
    }
  };

  return (
    <div className="min-h-screen grid lg:grid-cols-2" data-testid="admin-login">
      <div className="hidden lg:block relative overflow-hidden bg-slate-900">
        <img
          src="https://images.unsplash.com/photo-1760872646618-13594fc00567?crop=entropy&cs=srgb&fm=jpg&q=85"
          alt=""
          className="absolute inset-0 w-full h-full object-cover opacity-40"
        />
        <div className="absolute inset-0 bg-gradient-to-br from-slate-900/80 via-slate-900/40 to-transparent" />
        <div className="relative z-10 h-full flex flex-col justify-between p-12">
          <Logo inverse />
          <div>
            <h2 className="font-display text-4xl font-bold text-white leading-tight">
              Publish trusted <br /> notifications.
            </h2>
            <p className="mt-3 text-slate-300 text-sm max-w-sm">
              The admin console for Naukri Suchna — where verified government job updates get out to lakhs of aspirants.
            </p>
          </div>
        </div>
      </div>

      <div className="flex items-center justify-center p-6 sm:p-10 bg-white">
        <form onSubmit={submit} className="w-full max-w-sm">
          <div className="lg:hidden mb-8"><Logo /></div>
          <div className="text-[11px] font-bold tracking-[0.16em] uppercase text-orange-600 mb-2">
            Admin Console
          </div>
          <h1 className="font-display text-3xl font-bold text-slate-900 tracking-tight">Sign in</h1>
          <p className="mt-2 text-sm text-slate-500">
            Only authorized administrators can publish notifications.
          </p>

          <div className="mt-8 space-y-4">
            <label className="block">
              <span className="text-xs font-bold tracking-wide uppercase text-slate-600">Email</span>
              <div className="mt-1.5 relative">
                <Mail size={16} className="absolute left-3 top-3 text-slate-400" />
                <input
                  data-testid="admin-email"
                  type="email"
                  required
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  className="w-full h-11 pl-9 pr-3 border border-slate-300 rounded-md text-sm focus:border-slate-900 focus:ring-2 focus:ring-slate-900/10 outline-none"
                />
              </div>
            </label>
            <label className="block">
              <span className="text-xs font-bold tracking-wide uppercase text-slate-600">Password</span>
              <div className="mt-1.5 relative">
                <Lock size={16} className="absolute left-3 top-3 text-slate-400" />
                <input
                  data-testid="admin-password"
                  type="password"
                  required
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  className="w-full h-11 pl-9 pr-3 border border-slate-300 rounded-md text-sm focus:border-slate-900 focus:ring-2 focus:ring-slate-900/10 outline-none"
                />
              </div>
            </label>
          </div>

          {error && (
            <div className="mt-4 p-3 border border-red-200 bg-red-50 text-red-800 text-sm rounded-md" data-testid="admin-login-error">
              {error}
            </div>
          )}

          <button
            data-testid="admin-login-submit"
            type="submit"
            disabled={submitting}
            className="mt-6 w-full h-11 bg-slate-900 text-white rounded-md text-sm font-semibold hover:bg-slate-800 transition-colors inline-flex items-center justify-center gap-2 disabled:opacity-70"
          >
            <LogIn size={16} />
            {submitting ? "Signing in…" : "Sign in"}
          </button>

          <p className="mt-6 text-xs text-slate-400">
            Contact your system administrator if you don't have access.
          </p>
        </form>
      </div>
    </div>
  );
}
